// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-CRX-87 Phase 5 (design D-CRX-87b): more than one prescription under one enrollment, and the
// enrollment's expiry. C# twins: functions/CareRx/CareRxRxAttachFunctions.cs (TherapyRxResponse,
// TherapyRxItem, AttachResponse) and CareRxEnrollmentMode.cs (CombineTarget).

import { jsonRequest as req } from './http'

/** The open, not-expired same-drug enrollment a new prescription would attach to (PA feed + door picker). */
export interface CombineTarget {
  therapy_id: string
  /** Display-only "Enrollment ref" (ENR-…). */
  therapy_ref: string
  /** YYYY-MM-DD; null when the pharmacy has no validity days (never expires). */
  expires_on: string | null
}

export interface TherapyRxItem {
  rx_number: string
  drug_name: string | null
  /** anchor = the prescription the enrollment was started from; attached = joined later. */
  role: 'anchor' | 'attached'
  attached_at: string | null
  source: AttachSource | null
}

export interface TherapyRxResponse {
  therapy_id: string
  therapy_ref: string
  /** The pharmacy combines same-drug prescriptions (SaaS admin → CareRx). */
  combine: boolean
  validity_days: number | null
  /** YYYY-MM-DD; null = never expires. */
  expires_on: string | null
  expired: boolean
  items: TherapyRxItem[]
}

/** Where an attach was made: the PA feed's offer, the enrollment door's offer, or the review drawer's Add Rx. */
export type AttachSource = 'pa_feed' | 'door' | 'drawer'

export interface AttachRxResponse {
  therapy_id: string
  therapy_ref: string
  rx_number: string
  attached_at: string
  source: AttachSource
  /** Owner 2026-10-21 (A2): the enrollment went back to Clinical Review. */
  review_reopened: boolean
  /** Baseline labs the re-check found missing or expired (non-empty = on the Lab Tracker). */
  missing_labs: string[]
}

/** GET carerx/therapies/{id}/rx/eligible — what the drawer's optional Add Rx may add. */
export interface EligibleRx { rx_number: string; drug_name: string | null; received_at: string | null }
export interface EligibleRxResponse { therapy_id: string; combine: boolean; can_add: boolean; items: EligibleRx[] }

export const listTherapyRx = (therapyId: string) =>
  req<TherapyRxResponse>('GET', `/carerx/therapies/${therapyId}/rx`)

export const attachTherapyRx = (therapyId: string, rxNumber: string, source: AttachSource, reason?: string) =>
  req<AttachRxResponse>('POST', `/carerx/therapies/${therapyId}/rx`, { rx_number: rxNumber, source, reason: reason || undefined })

export const listEligibleTherapyRx = (therapyId: string) =>
  req<EligibleRxResponse>('GET', `/carerx/therapies/${therapyId}/rx/eligible`)

export const detachTherapyRx = (therapyId: string, rxNumber: string, reason: string) =>
  req<{ therapy_id: string; rx_number: string; detached: true }>(
    'POST', `/carerx/therapies/${therapyId}/rx/${encodeURIComponent(rxNumber)}/detach`, { reason })
